import React, { useEffect, useState } from 'react';

import styles from './ChangePassword.module.scss';

import { AuthFormLayout } from 'layouts/AuthFormLayout/AuthFormLayout';
import { Navigate, useNavigate } from 'react-router-dom';
import { routes } from 'enums';
import { CustomInput } from 'components/CustomInput';
import { useActions, useAppDispatch, useAppSelector } from 'hooks';
import {
  accessTokenSelect,
  authActions,
  authThunk,
  errorSelect,
  resetTokenSelect,
  resetVerifiedSelect,
} from 'state';

/** Mirrors ResetPasswordDto server-side, so the user hears about it here. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Final step of the forgot-password flow: choose a new password.
 *
 * Two things were wrong here beyond the missing guard. The fields were
 * labelled "Password" and "New password" but this screen is reached without a
 * session — there is no current password to supply — and the request sent
 * `{ password, newPassword, token }` while ResetPasswordDto expects
 * `{ token, password, passwordConfirm }` and matches the two server-side. So
 * every submission that got this far failed on "Password confirm required",
 * and the thunk swallowed the error, so the form did nothing at all.
 */
export const ChangePassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();
  const token = useAppSelector(accessTokenSelect);
  const error = useAppSelector(errorSelect);
  const dispatch = useAppDispatch();
  const { changePassword } = useActions(authThunk);
  const resetToken: string = useAppSelector(resetTokenSelect);
  const resetVerified: boolean = useAppSelector(resetVerifiedSelect);

  const handleSubmit = async () => {
    // Checked here as well as server-side so the admin is told immediately,
    // rather than after a round trip that returns a field-level 400.
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      dispatch(
        authActions.setError(
          `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
        ),
      );

      return;
    }

    if (newPassword !== confirmPassword) {
      dispatch(authActions.setError('The two passwords do not match.'));

      return;
    }

    await changePassword({
      changeData: {
        token: resetToken,
        password: newPassword,
        passwordConfirm: confirmPassword,
      },
      navigate,
    });
  };

  useEffect(() => {
    if (token) {
      navigate(routes.MAIN.INIT);
    }
  }, [token]);

  // A-01: this is a public route, so without this guard the form rendered for
  // anyone who typed the URL. It only looked safe before because an unrelated
  // bug in PublicLayout bounced every anonymous visitor to /login first; once
  // that was fixed, this became the thing standing in the way. Reaching it
  // requires a reset code that the server has already confirmed.
  if (!resetToken || !resetVerified) {
    return <Navigate to={routes.AUTH.LOG_IN} replace />;
  }

  return (
    <AuthFormLayout
      title={'Change password'}
      subtitle={'To change the current password, enter the new password in both fields'}
      onSubmit={handleSubmit}
    >
      <div style={{ width: '70%', margin: '0 auto' }}>
        <CustomInput
          error={error}
          clearError={() => dispatch(authActions.clearError())}
          title={'New password'}
          value={newPassword}
          onChange={setNewPassword}
          password
        />
      </div>

      <div style={{ width: '70%', margin: '0 auto' }}>
        <CustomInput
          error={error}
          clearError={() => dispatch(authActions.clearError())}
          title={'Confirm new password'}
          value={confirmPassword}
          onChange={setConfirmPassword}
          password
        />
      </div>
      <div
        style={{
          height: 26,
          fontSize: 15,
          color: 'red',
          //bottom: 20,
          margin: '0 auto',
          //marginTop: 5,
        }}
      >
        {error}
      </div>
    </AuthFormLayout>
  );
};
